import { Dialog, Portal, Box } from "@chakra-ui/react";

import { MouseEventHandler, ReactNode, Ref, useCallback, useRef } from "react";

import { asSlotComponent } from "../internal/v3-slot";
import { Button } from "../button/button";
import {
  resolveTestId,
  scopedTestIds,
  type TestIdProps
} from "../utils/test-ids";

const DialogBackdrop = asSlotComponent(Dialog.Backdrop);
const DialogPositioner = asSlotComponent(Dialog.Positioner);
const DialogContent = asSlotComponent(Dialog.Content);
const DialogHeader = asSlotComponent(Dialog.Header);
const DialogTitle = asSlotComponent(Dialog.Title);
const DialogBody = asSlotComponent(Dialog.Body);
const DialogDescription = asSlotComponent(Dialog.Description);
const DialogFooter = asSlotComponent(Dialog.Footer);

// Padding is set per-slot below, so flush the recipe's.
const flushDialogSlotPadding = {
  p: 0,
  px: 0,
  py: 0,
  pt: 0,
  pb: 0,
  pl: 0,
  pr: 0
};

export interface AlertDialogProps extends TestIdProps {
  open?: boolean;
  onClose?: () => void;
  header: string;
  body: string;
  buttonPrimary?: string;
  buttonSecondary?: string;
  buttonPrimaryVariant?: string;
  primaryDestructive?: boolean;
  buttonSecondaryVariant?: string;
  secondaryDestructive?: boolean;
  onPrimaryClickHandler?: MouseEventHandler<HTMLButtonElement>;
  onSecondaryClickHandler?: MouseEventHandler<HTMLButtonElement>;
}

interface ButtonHandlerProps {
  children?: ReactNode;
  variant?: string;
  destructive?: boolean;
  onClickHandler?: MouseEventHandler<HTMLButtonElement>;
  buttonRef?: Ref<HTMLButtonElement>;
  "data-testid"?: string;
}

function AlertDialog(props: AlertDialogProps) {
  const {
    open,
    onClose,
    header,
    body,
    buttonPrimary,
    buttonSecondary,
    buttonPrimaryVariant,
    primaryDestructive,
    buttonSecondaryVariant,
    secondaryDestructive,
    onPrimaryClickHandler,
    onSecondaryClickHandler
  } = props;

  // Focus the least-destructive (secondary) action on open so a stray
  // Enter/Space can't fire the primary — which may be destructive.
  const leastDestructiveRef = useRef<HTMLButtonElement>(null);

  const t = scopedTestIds(resolveTestId(props));

  const handleOpenChange = useCallback(
    (details: { open: boolean }) => {
      if (!details.open) {
        onClose?.();
      }
    },
    [onClose]
  );

  const ButtonHandler = ({
    children,
    variant,
    destructive,
    onClickHandler,
    buttonRef,
    "data-testid": testId
  }: ButtonHandlerProps): JSX.Element => {
    return (
      <Button
        ref={buttonRef}
        variant={variant}
        destructive={destructive}
        onClick={onClickHandler}
        {...(testId ? { "data-testid": testId } : {})}
        size={{ base: "lg", xl: "md" }}
        px={6}
        py={3}
        minW="116px !important"
        maxW={40}
      >
        {children}
      </Button>
    );
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={handleOpenChange}
      placement="center"
      role="alertdialog"
      initialFocusEl={() => leastDestructiveRef.current}
    >
      <Portal>
        <DialogBackdrop />
        <DialogPositioner>
          <DialogContent
            m={4}
            py={6}
            px={4}
            gap={6}
            w={{ base: "full", lg: 448 }}
            minW={288}
            maxW={448}
            minH={60}
            boxShadow="lg"
            {...t.props("content")}
          >
            <Box
              display="flex"
              flexDirection="column"
              minH={{ base: 120, lg: 128 }}
              justifyContent="center"
              alignItems="flex-start"
              gap={2}
              alignSelf="stretch"
            >
              <DialogHeader
                {...flushDialogSlotPadding}
                alignItems="flex-start"
                alignSelf="stretch"
              >
                {/* Wires the dialog's aria-labelledby; lg + semibold matches the prior header. */}
                <DialogTitle fontSize="lg">{header}</DialogTitle>
              </DialogHeader>
              <DialogBody {...flushDialogSlotPadding} display="contents">
                {/* Wires aria-describedby; color inherit keeps the prior (non-muted) body color. */}
                <DialogDescription color="inherit" fontSize="sm">
                  {body}
                </DialogDescription>
              </DialogBody>
            </Box>
            <DialogFooter
              {...flushDialogSlotPadding}
              alignItems="flex-start"
              gap={6}
              alignSelf="stretch"
            >
              <>
                {buttonSecondary && (
                  <ButtonHandler
                    buttonRef={leastDestructiveRef}
                    variant={buttonSecondaryVariant}
                    destructive={secondaryDestructive}
                    onClickHandler={onSecondaryClickHandler}
                    data-testid={t.child("cancel-button")}
                  >
                    {buttonSecondary}
                  </ButtonHandler>
                )}
                {buttonPrimary && (
                  <ButtonHandler
                    variant={buttonPrimaryVariant}
                    destructive={primaryDestructive}
                    onClickHandler={onPrimaryClickHandler}
                    data-testid={t.child("confirm-button")}
                  >
                    {buttonPrimary}
                  </ButtonHandler>
                )}
              </>
            </DialogFooter>
          </DialogContent>
        </DialogPositioner>
      </Portal>
    </Dialog.Root>
  );
}

export { AlertDialog };
